import {
  alpha,
  lighten,
  LinkProps,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
} from '@mui/material';
import React from 'react';
import { NormalizedGroup } from '../../../../../redux/structure/GroupsAdapter';
import EntityActions from './EntityActions/EntityActions';
import { useAppSelector } from '../../../../../redux/store.model';
import { getAllStoreChildIdsFromGroupSelector } from '../../../../../redux/structure/selectors/Stores.selector';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BusinessLayerGroupType } from '../../../../../redux/structure/util/getTypeFromGroup';
import useAlarmsByGroup from '../../../../../redux/alarm/hooks/useAlarmsByGroup';
import useDeviceStatusFromGroup from '../../../../../service/device/CommanderAPI/hooks/useDeviceStatusFromGroup';
import useViewLocationsPath from '../../../../../shared/hooks/useViewLocationsPath';

interface StructureListItemProps {
  entity?: NormalizedGroup & { workspaceId?: string };
  /* Filter out the items that have alarms when the filter is active */
  isFilterTemperatureActive?: boolean;
  /* Filter out the items that have commander status failing when the filter is active */
  isFilterCommanderStatusFalling?: boolean;
}
function StructureListItem(props: Readonly<StructureListItemProps>) {
  const { t } = useTranslation();
  const { entity, isFilterTemperatureActive, isFilterCommanderStatusFalling } =
    props;

  const { data, isLoading } = useAlarmsByGroup({
    groupId: entity?.uuid,
  });
  const hasAlarms = !!data?.length;
  const { numberFailingMqttConnection, isLoading: isLoadingDeviceStatus } =
    useDeviceStatusFromGroup(entity?.uuid);
  const hasCommanderStatusFailing = !!numberFailingMqttConnection;

  const hide =
    (isFilterTemperatureActive && !hasAlarms && !isLoading) ||
    (isFilterCommanderStatusFalling &&
      !hasCommanderStatusFailing &&
      !isLoadingDeviceStatus);

  const { marketId } = useParams<{ marketId: string }>();
  const { getViewLocationsPath } = useViewLocationsPath();
  const storesIds = useAppSelector((state) =>
    getAllStoreChildIdsFromGroupSelector(state, {
      workspaceId: entity?.workspaceId ?? marketId,
      groupId: entity?.uuid ?? '',
    })
  );
  const secondaryText =
    entity?.businessLayerType === BusinessLayerGroupType.Store
      ? undefined
      : t('view_locations.store_inside', {
          count: storesIds?.length,
        });

  // http://localhost:3013/#/markets/4ba68ae6-4a1d-47ef-ba07-2440508a0c89/view-locations/46dbd266-60cf-4a86-a306-e95cd4fd6c7f
  const url = getViewLocationsPath(entity?.uuid, entity?.workspaceId);
  if (hide) return null;
  return (
    <ListItem
      disablePadding
      secondaryAction={<EntityActions {...props} />}
      sx={{
        '& .MuiListItemText-root': {
          maxWidth: 'calc(100% - 270px)',
        },
      }}
    >
      <StyledListItemButton href={url}>
        <ListItemText
          sx={{
            ml: 2,
            '& span': {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
            '& .MuiListItemText-secondary': {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
          }}
          secondary={secondaryText}
        >
          {entity?.name}
        </ListItemText>
      </StyledListItemButton>
    </ListItem>
  );
}

export const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'error',
})<LinkProps & { error?: boolean }>(({ theme }) => ({
  backgroundColor: lighten(theme.palette.background.paper, 0.1),

  borderLeft: `4px solid ${theme.palette.text.primary}`,

  color: theme.palette.text.primary,
  my: 0.5,
  display: 'flex',
  justifyContent: 'space-between',

  variants: [
    {
      props: ({ error }) => error,

      style: {
        backgroundColor: alpha(theme.palette.primary.main, 0.4),
      },
    },
    {
      props: ({ error }) => error,

      style: {
        borderLeft: {
          borderLeft: theme.palette.primary.main,
        },
      },
    },
  ],
}));

export default StructureListItem;
